import React, { useState } from 'react';
import { BarChart3, Briefcase, Calendar, CheckCircle, Download, Home, Mail, Play, Target, TrendingDown, TrendingUp, Users } from 'lucide-react';

// Use relative URLs on production (same-origin) to avoid CORS issues
const BACKEND_URL = window.location.hostname === 'sierrascala.com' || window.location.hostname === 'www.sierrascala.com'
  ? ''
  : process.env.REACT_APP_BACKEND_URL;

const MeadowsLandingPage = () => {
  const [accessGranted, setAccessGranted] = useState(() => {
    return typeof window !== 'undefined' && sessionStorage.getItem('meadows_access') === 'granted';
  });
  const [codeInput, setCodeInput] = useState('');
  const [codeError, setCodeError] = useState(false);
  const [requestName, setRequestName] = useState('');
  const [requestCompany, setRequestCompany] = useState('');
  const [requestEmail, setRequestEmail] = useState('');
  const [requestSent, setRequestSent] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);

  const ACCESS_CODE = 'meadows2026';

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (codeInput.toLowerCase().trim() === ACCESS_CODE.toLowerCase()) {
      sessionStorage.setItem('meadows_access', 'granted');
      setAccessGranted(true);
      setCodeError(false);
    } else {
      setCodeError(true);
    }
  };

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    if (!requestName.trim() || !requestEmail.trim()) return;
    setRequestLoading(true);
    try {
      await fetch(`${BACKEND_URL}/api/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: requestName, email: requestEmail, company: requestCompany || '', proposal: 'meadows' })
      });
    } catch (err) {}
    setRequestSent(true);
    setRequestLoading(false);
  };

  if (!accessGranted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-teal-950 to-slate-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-4">SS</div>
            <h1 className="text-2xl font-bold text-white mb-2">Sierra Scala</h1>
            <p className="text-slate-300 text-sm">Social Media & Content Strategist</p>
          </div>
          <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-8 border border-white/40 mb-6" data-testid="meadows-access-card">
            <h2 className="text-xl font-bold text-white text-center mb-2">This Proposal is Private</h2>
            <p className="text-slate-200 text-sm text-center mb-6">Enter the access code to view my strategy for the Meadows Institute.</p>
            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <input type="text" value={codeInput} onChange={(e) => { setCodeInput(e.target.value); setCodeError(false); }}
                placeholder="Enter access code"
                className={`w-full px-4 py-3 bg-white/30 border ${codeError ? 'border-red-500' : 'border-white/50'} rounded-xl text-white placeholder-white/70 focus:outline-none focus:border-teal-400 text-center tracking-widest`}
                data-testid="meadows-code-input" />
              {codeError && <p className="text-red-400 text-xs text-center">Incorrect code. Please try again.</p>}
              <button type="submit" className="w-full py-3 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white font-semibold rounded-xl transition-all" data-testid="meadows-code-submit">View Proposal</button>
            </form>
          </div>
          <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-6 border border-white/35" data-testid="meadows-request-card">
            {!requestSent ? (
              <>
                <h3 className="text-white font-semibold text-center mb-1">Don't have the code?</h3>
                <p className="text-slate-300 text-xs text-center mb-4">Request access and I'll send it to you directly.</p>
                <form onSubmit={handleRequestAccess} className="space-y-3">
                  <input type="text" value={requestName} onChange={(e) => setRequestName(e.target.value)} placeholder="Your name" required className="w-full px-4 py-2.5 bg-white/30 border border-white/50 rounded-lg text-white placeholder-white/70 focus:outline-none focus:border-teal-400 text-sm" />
                  <input type="text" value={requestCompany} onChange={(e) => setRequestCompany(e.target.value)} placeholder="Company (optional)" className="w-full px-4 py-2.5 bg-white/30 border border-white/50 rounded-lg text-white placeholder-white/70 focus:outline-none focus:border-teal-400 text-sm" />
                  <input type="email" value={requestEmail} onChange={(e) => setRequestEmail(e.target.value)} placeholder="Your email" required className="w-full px-4 py-2.5 bg-white/30 border border-white/50 rounded-lg text-white placeholder-white/70 focus:outline-none focus:border-teal-400 text-sm" />
                  <button type="submit" disabled={requestLoading} className="w-full py-2.5 bg-white/30 hover:bg-white/40 border border-white/50 text-white font-medium rounded-lg transition-all text-sm flex items-center justify-center gap-2">
                    <Mail size={16} /> {requestLoading ? 'Sending...' : 'Request Access Code'}
                  </button>
                </form>
                <div className="mt-4 pt-4 border-t border-white/20 text-center">
                  <p className="text-slate-300 text-xs mb-2">Or reach out directly:</p>
                  <a href="mailto:me@sierrascala.com" className="text-teal-400 hover:text-teal-300 text-sm font-medium">me@sierrascala.com</a>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3"><CheckCircle className="text-green-400" size={24} /></div>
                <h3 className="text-white font-semibold mb-1">Request Sent!</h3>
                <p className="text-slate-400 text-sm">I'll send you the access code shortly.</p>
              </div>
            )}
          </div>
          <div className="text-center mt-6"><a href="/" className="text-slate-500 hover:text-slate-300 text-sm flex items-center justify-center gap-1"><Home size={14} /> View Full Portfolio</a></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen" data-testid="meadows-proposal-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-teal-950 to-slate-900 text-white py-20 px-4 sm:px-6 lg:px-8" data-testid="meadows-hero">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-4 py-1.5 bg-teal-500/20 border border-teal-500/40 rounded-full text-teal-300 text-xs font-semibold tracking-wider uppercase mb-6">Custom Proposal</span>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Social Media & Content Strategy Proposal</h1>
          <h2 className="text-xl md:text-2xl text-teal-300 font-medium mb-6">For: Meadows Mental Health Policy Institute</h2>
          <p className="text-slate-300 max-w-2xl mx-auto text-base mb-2">Prepared by <strong className="text-white">Sierra Scala</strong> — Social Media Strategist & Multimedia Storyteller</p>
          <p className="text-slate-400 text-sm">Dallas-Fort Worth, TX | Ready to amplify Texas's leadership in mental health policy</p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="meadows-intro">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl p-8 border border-teal-200">
            <p className="text-gray-700 text-lg leading-relaxed">
              I'm excited to partner with the Meadows Institute to build brand awareness, engage new audiences, and turn complex policy and clinical work into clear, compelling narratives that drive real impact across Texas. With <strong>5+ years crafting data-driven social strategies and video content</strong> that deliver measurable results (<strong>850K+ reach, tripled leads, viral campaigns</strong>), I'm prepared to lead your social media and content efforts as your Social Media and Content Manager.
            </p>
          </div>
        </div>
      </section>

      {/* Understanding MMHPI */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50" data-testid="meadows-understanding">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">The Organization</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-2">Understanding the Meadows Institute</h2>
          </div>
          <div className="bg-white rounded-xl p-6 border border-slate-200 mb-8">
            <p className="text-gray-700 leading-relaxed">
              Your mission — making Texas the national leader in effective, efficient mental health care — is critical and timely. Your current content (thoughtful explainers, data briefs, and stakeholder updates) is already strong, but there's huge opportunity to <strong>expand reach through video/multimedia storytelling</strong>, consistent platform optimization, and audience-specific engagement.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="font-bold text-gray-900 mb-2">Mission</h3>
              <p className="text-sm text-gray-600">Data-driven, nonpartisan nonprofit supporting policies and programs that help Texans obtain effective, efficient mental health care when and where they need it.</p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="font-bold text-gray-900 mb-2">Vision</h3>
              <p className="text-sm text-gray-600">For Texas to be the national leader in treating all people with mental health needs — driving systemic transformation through policy guidance and community partnerships.</p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="font-bold text-gray-900 mb-2">Priority Areas</h3>
              <div className="flex flex-wrap gap-2 mt-2">
                {['Adults', 'Children & Youth', 'Schools & Education', 'Justice & Health', 'Veterans'].map(area => (
                  <span key={area} className="bg-teal-50 text-teal-700 px-2.5 py-1 rounded-full text-xs font-medium border border-teal-200">{area}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Website & SEO Audit */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="meadows-website-audit">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Website & SEO Analysis</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-2">mmhpi.org Digital Presence Audit</h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">I analyzed mmhpi.org to identify content optimization opportunities and untapped strategies for expanding the Institute's digital reach.</p>
          </div>

          {/* Technical SEO Score Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-5 border border-green-200 text-center" data-testid="audit-wordpress">
              <div className="text-3xl font-bold text-green-600 mb-1">WordPress</div>
              <div className="text-sm text-gray-700 font-medium">CMS Platform</div>
              <p className="text-xs text-gray-500 mt-2">Solid SEO-friendly foundation with clean URL structure (/work/, /topics/, /project/). Good crawlability.</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-5 border border-yellow-200 text-center" data-testid="audit-metadata">
              <div className="text-3xl font-bold text-yellow-600 mb-1">Partial</div>
              <div className="text-sm text-gray-700 font-medium">Meta Descriptions</div>
              <p className="text-xs text-gray-500 mt-2">Many pages lack unique meta descriptions. Homepage title is generic "MMHPI" — missing keyword-rich descriptions.</p>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-5 border border-red-200 text-center" data-testid="audit-schema">
              <div className="text-3xl font-bold text-red-600 mb-1">None</div>
              <div className="text-sm text-gray-700 font-medium">Schema Markup</div>
              <p className="text-xs text-gray-500 mt-2">No Organization, NonProfit, or Event schema detected. Missing rich snippets for case studies and reports.</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-5 border border-yellow-200 text-center" data-testid="audit-pdf">
              <div className="text-3xl font-bold text-yellow-600 mb-1">PDF-Heavy</div>
              <div className="text-sm text-gray-700 font-medium">Content Format</div>
              <p className="text-xs text-gray-500 mt-2">Reports published as PDFs (/wp-content/uploads/) instead of web pages — limits social sharing and SEO indexing.</p>
            </div>
          </div>

          {/* Content Strategy Analysis */}
          <div className="bg-slate-50 rounded-2xl p-6 md:p-8 border border-slate-200 mb-8" data-testid="audit-content-analysis">
            <h3 className="font-bold text-gray-900 mb-2 text-lg flex items-center gap-2">
              <Target className="text-slate-600" size={22} /> Content & SEO Analysis of mmhpi.org
            </h3>
            <p className="text-sm text-gray-500 mb-6">Based on an analysis of mmhpi.org's site structure, content types, metadata, and social integration — here's what I found:</p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-slate-300">
                    <th className="text-left py-3 px-2 text-gray-700 font-semibold">Element</th>
                    <th className="text-left py-3 px-2 text-gray-700 font-semibold">Current State</th>
                    <th className="text-left py-3 px-2 text-gray-700 font-semibold">Opportunity</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-200">
                    <td className="py-3 px-2 text-gray-500 text-xs font-medium">Homepage</td>
                    <td className="py-3 px-2 text-gray-800 font-medium">Embedded X/Twitter feed, interactive stats (Veterans, Adults, Children), priority area links</td>
                    <td className="py-3 px-2"><span className="text-yellow-600 text-xs">Twitter embed limits exposure. Add Instagram feed, video highlights, and email signup CTA above the fold.</span></td>
                  </tr>
                  <tr className="border-b border-slate-200">
                    <td className="py-3 px-2 text-gray-500 text-xs font-medium">Case Studies</td>
                    <td className="py-3 px-2 text-gray-800 font-medium">Strong collection: Schools Roadmap, Dallas Smart Justice, Blue Chip Program</td>
                    <td className="py-3 px-2"><span className="text-green-600 text-xs">Excellent content — but buried in navigation. Repurpose as social carousels, video summaries, and LinkedIn articles.</span></td>
                  </tr>
                  <tr className="border-b border-slate-200">
                    <td className="py-3 px-2 text-gray-500 text-xs font-medium">Reports & PDFs</td>
                    <td className="py-3 px-2 text-gray-800 font-medium">Extensive library: CoCM Metrics, Rural MCOTs Toolkit, Youth Policy Solutions</td>
                    <td className="py-3 px-2"><span className="text-red-600 text-xs">Critical gap: PDFs don't generate social previews, backlinks, or SEO value. Convert key findings into web pages + social-ready graphics.</span></td>
                  </tr>
                  <tr className="border-b border-slate-200">
                    <td className="py-3 px-2 text-gray-500 text-xs font-medium">Blog / News</td>
                    <td className="py-3 px-2 text-gray-800 font-medium">/topics/announcements/ section with event recaps and conference highlights</td>
                    <td className="py-3 px-2"><span className="text-yellow-600 text-xs">Good cadence but text-heavy. Add visuals, pull quotes, and social share buttons. No evergreen "explainer" content for organic search.</span></td>
                  </tr>
                  <tr className="border-b border-slate-200">
                    <td className="py-3 px-2 text-gray-500 text-xs font-medium">HeadsUp Newsletter</td>
                    <td className="py-3 px-2 text-gray-800 font-medium">Policy advisory newsletter — strong thought leadership asset</td>
                    <td className="py-3 px-2"><span className="text-green-600 text-xs">Repurpose newsletter highlights as LinkedIn posts, X threads, and Instagram carousel "policy briefs." Huge untapped content mine.</span></td>
                  </tr>
                  <tr className="border-b border-slate-200">
                    <td className="py-3 px-2 text-gray-500 text-xs font-medium">Social Integration</td>
                    <td className="py-3 px-2 text-gray-800 font-medium">Only X/Twitter embedded on homepage. No Facebook, Instagram, LinkedIn, or YouTube integration.</td>
                    <td className="py-3 px-2"><span className="text-red-600 text-xs">Major gap: site visitors have no pathway to follow on other platforms. Add social links in header/footer + share buttons on every page.</span></td>
                  </tr>
                  <tr>
                    <td className="py-3 px-2 text-gray-500 text-xs font-medium">Multimedia</td>
                    <td className="py-3 px-2 text-gray-800 font-medium">Minimal video or visual storytelling on website. Static images only.</td>
                    <td className="py-3 px-2"><span className="text-red-600 text-xs">No embedded video content on the site despite YouTube channel existing. Add video highlights to case study pages and homepage.</span></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* What's Missing */}
            <div className="bg-white rounded-xl p-5 border border-slate-200 mb-4">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <TrendingDown className="text-red-500" size={18} /> SEO Content Gaps I'd Address
              </h4>
              <div className="grid md:grid-cols-2 gap-3">
                {[
                  { gap: 'No evergreen "What is..." content', fix: 'Pages like "What is Collaborative Care?" or "Understanding Youth Mental Health Crisis" would capture high-intent search traffic' },
                  { gap: 'PDF reports not indexed for search', fix: 'Key report findings should be published as web pages with social meta tags, not locked in PDFs' },
                  { gap: 'No Open Graph tags for social sharing', fix: 'When someone shares a page on Facebook/LinkedIn, there\'s no custom preview image or description' },
                  { gap: 'Missing schema markup for events', fix: 'Conferences and events could appear in Google\'s rich results with proper Event schema' },
                  { gap: 'No video SEO on YouTube channel', fix: 'Existing videos lack optimized titles, descriptions, timestamps, and cross-links to mmhpi.org resources' },
                  { gap: 'Single social embed (X/Twitter only)', fix: 'Homepage should showcase content from all active platforms, not just one. Cross-platform social proof drives follows.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2 bg-red-50 rounded-lg p-3 border border-red-100">
                    <TrendingDown size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-800">{item.gap}</p>
                      <p className="text-xs text-gray-500">{item.fix}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Wins */}
            <div className="bg-white rounded-xl p-5 border border-slate-200">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <TrendingUp className="text-green-500" size={18} /> Website Quick Wins (First 30 Days)
              </h4>
              <div className="grid md:grid-cols-2 gap-3">
                {[
                  { action: 'Add social links and share buttons site-wide', impact: 'Instant cross-platform discoverability from every page' },
                  { action: 'Convert top 5 PDF reports into web pages', impact: 'Unlock SEO value and social shareability for highest-traffic content' },
                  { action: 'Add Organization + NonProfit schema to homepage', impact: 'Rich search results with logo, social links, and description' },
                  { action: 'Embed video highlights on case study pages', impact: 'Increase time-on-page and give visitors multimedia entry points' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2 bg-green-50 rounded-lg p-3 border border-green-100">
                    <TrendingUp size={14} className="text-green-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-800">{item.action}</p>
                      <p className="text-xs text-gray-500">{item.impact}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Audit — Expanded */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50" data-testid="meadows-audit">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Social Media Audit</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-2">Platform-by-Platform Analysis</h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">A detailed look at each platform's current state, audience fit, and growth opportunity for MMHPI.</p>
          </div>

          {/* Platform Deep Dives */}
          <div className="space-y-6 mb-8">

            {/* Facebook - Expanded */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200" data-testid="audit-facebook">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-10 bg-blue-500 rounded-xl text-white flex items-center justify-center font-bold">Fb</span>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">Facebook — Biggest Untapped Audience</h3>
                  <p className="text-sm text-gray-500">Mental health is one of the most-engaged topics on Facebook. Families, caregivers, and community members turn here first.</p>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <p className="text-xs text-blue-600 font-semibold uppercase mb-1">Current State</p>
                  <p className="text-sm text-gray-700">Minimal presence. Posts are infrequent and primarily link-shares without visual or video content. Low engagement relative to audience potential.</p>
                </div>
                <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                  <p className="text-xs text-red-600 font-semibold uppercase mb-1">Gap</p>
                  <p className="text-sm text-gray-700">No video content, no community-building posts, no Facebook Events for conferences. The 35-65 caregiver demographic — MMHPI's ideal community audience — lives on Facebook.</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                  <p className="text-xs text-green-600 font-semibold uppercase mb-1">My Plan</p>
                  <p className="text-sm text-gray-700">Weekly video posts (program highlights, policy explainers). Facebook Events for conferences. Community resource posts that drive shares. Target: 2x engagement in 90 days.</p>
                </div>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <p className="text-xs text-gray-500"><strong className="text-gray-700">Why Facebook matters for MMHPI:</strong> Mental health support groups on Facebook have millions of members. Policy content reformatted as "5 things every Texas parent should know about school mental health" gets shared organically. Facebook is where families discover resources — and MMHPI has the best resources in Texas.</p>
              </div>
            </div>

            {/* Instagram */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200" data-testid="audit-instagram">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl text-white flex items-center justify-center font-bold">Ig</span>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">Instagram @meadowsinst — 868 followers, 534 posts</h3>
                  <p className="text-sm text-gray-500">Solid post count shows commitment, but follower growth has stalled. Reels and video are the unlock.</p>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                  <p className="text-xs text-purple-600 font-semibold uppercase mb-1">Current State</p>
                  <p className="text-sm text-gray-700">534 posts but only 868 followers suggests low engagement and limited reach. Content is primarily static graphics and link posts without Reels or Stories.</p>
                </div>
                <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                  <p className="text-xs text-red-600 font-semibold uppercase mb-1">Gap</p>
                  <p className="text-sm text-gray-700">No Reels (Instagram's #1 growth driver). No Stories for behind-the-scenes. No carousel explainers. The 25-44 mental health advocate demographic is here but not being reached.</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                  <p className="text-xs text-green-600 font-semibold uppercase mb-1">My Plan</p>
                  <p className="text-sm text-gray-700">3x weekly Reels (policy explainers, impact stories, staff spotlights). Carousel posts for data/reports. Stories for events. Target: 3x follower growth in 6 months.</p>
                </div>
              </div>
            </div>

            {/* X/Twitter */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200" data-testid="audit-twitter">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-10 bg-sky-500 rounded-xl text-white flex items-center justify-center font-bold">X</span>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">X/Twitter @MeadowsInst — Strongest Channel</h3>
                  <p className="text-sm text-gray-500">Active professional presence with policy tweets, retweets, and event promotion. The foundation is solid.</p>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div className="bg-sky-50 rounded-xl p-4 border border-sky-100">
                  <p className="text-xs text-sky-600 font-semibold uppercase mb-1">Current State</p>
                  <p className="text-sm text-gray-700">Professional text + link sharing. Active engagement with partner orgs (@HHSGov, @PolicyCenterMMH). Primary social channel with embedded feed on homepage.</p>
                </div>
                <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-100">
                  <p className="text-xs text-yellow-600 font-semibold uppercase mb-1">Optimize</p>
                  <p className="text-sm text-gray-700">Posts are text-heavy with minimal visuals. Low likes/retweets suggest content isn't driving engagement. Threads and visual content could 2-3x reach.</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                  <p className="text-xs text-green-600 font-semibold uppercase mb-1">My Plan</p>
                  <p className="text-sm text-gray-700">Policy threads (5-7 tweet breakdowns). Visual quote cards. Live-tweeting events. Strategic hashtags: #TXMentalHealth #OkayToSay. Maintain + optimize, don't over-invest.</p>
                </div>
              </div>
            </div>

            {/* LinkedIn */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200" data-testid="audit-linkedin">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-10 bg-blue-600 rounded-xl text-white flex items-center justify-center font-bold">Li</span>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">LinkedIn — Where the Decision-Makers Are</h3>
                  <p className="text-sm text-gray-500">Legislators, healthcare executives, and policy makers — MMHPI's core stakeholder audience lives on LinkedIn.</p>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <p className="text-xs text-blue-600 font-semibold uppercase mb-1">Current State</p>
                  <p className="text-sm text-gray-700">Organization page exists with professional content. Could benefit from thought leadership posts from CEO/researchers and multimedia content.</p>
                </div>
                <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-100">
                  <p className="text-xs text-yellow-600 font-semibold uppercase mb-1">Optimize</p>
                  <p className="text-sm text-gray-700">Needs consistent cadence and multimedia. HeadsUp newsletter content should be repurposed here. Staff thought leadership is a major untapped asset.</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                  <p className="text-xs text-green-600 font-semibold uppercase mb-1">My Plan</p>
                  <p className="text-sm text-gray-700">Weekly long-form articles. Data visualizations from reports. CEO/researcher thought leadership. Conference recaps. Target: #1 growth channel for B2B reach.</p>
                </div>
              </div>
            </div>

            {/* YouTube */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200" data-testid="audit-youtube">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-10 bg-red-600 rounded-xl text-white flex items-center justify-center font-bold">YT</span>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">YouTube — Existing Library, Zero Optimization</h3>
                  <p className="text-sm text-gray-500">MMHPI has policy videos and interviews — they just need SEO optimization and short-form repurposing.</p>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                  <p className="text-xs text-red-600 font-semibold uppercase mb-1">Current State</p>
                  <p className="text-sm text-gray-700">Channel exists with policy videos and conference recordings. Titles are generic. No descriptions, tags, or timestamps for discoverability.</p>
                </div>
                <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-100">
                  <p className="text-xs text-yellow-600 font-semibold uppercase mb-1">Optimize</p>
                  <p className="text-sm text-gray-700">Low subscriber engagement. Videos not embedded on mmhpi.org. No YouTube Shorts created from long-form content. No cross-promotion.</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                  <p className="text-xs text-green-600 font-semibold uppercase mb-1">My Plan</p>
                  <p className="text-sm text-gray-700">Re-optimize all titles/descriptions. Create Shorts from interviews. Add timestamps + CTAs. Embed on website. Cross-promote on Instagram Reels.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Overall Opportunity */}
          <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-2xl p-6 text-white" data-testid="audit-opportunity">
            <h3 className="font-bold text-lg mb-3 text-center">The Big Picture</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white/15 rounded-xl p-4 text-center">
                <p className="font-bold text-2xl mb-1">Website</p>
                <p className="text-teal-100 text-sm">PDF-to-web content conversion + schema + social integration = stronger SEO foundation</p>
              </div>
              <div className="bg-white/15 rounded-xl p-4 text-center">
                <p className="font-bold text-2xl mb-1">Social</p>
                <p className="text-teal-100 text-sm">Shift from text-heavy X-only to multi-platform video storytelling = 3x audience reach</p>
              </div>
              <div className="bg-white/15 rounded-xl p-4 text-center">
                <p className="font-bold text-2xl mb-1">Content</p>
                <p className="text-teal-100 text-sm">Repurpose existing reports, case studies, and HeadsUp into social-first formats = more impact, same resources</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Proposed Strategy */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50" data-testid="meadows-strategy">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Proposed Strategy</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-2">Ready to Execute</h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">A full social media strategy that directly supports the role's responsibilities.</p>
          </div>

          {/* Content Pillars */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200 mb-8">
            <h3 className="font-bold text-gray-900 mb-6 text-lg flex items-center gap-2"><Target size={20} className="text-teal-600" /> Content Pillars</h3>
            <div className="grid md:grid-cols-4 gap-4">
              {[
                { num: '1', title: 'Policy Made Simple', desc: 'FAQs, one-pagers turned into carousels/Reels — making complex policy accessible to everyone.', color: 'bg-teal-600' },
                { num: '2', title: 'Texas Impact Stories', desc: 'Real-people stories + data visuals from programs like Blue Chip, Hackett Center, and Okay to Say.', color: 'bg-cyan-600' },
                { num: '3', title: 'Data-Driven Insights', desc: 'Infographics + short explainers turning research reports into shareable, visual content.', color: 'bg-blue-600' },
                { num: '4', title: 'Community & Awareness', desc: 'Q&A sessions, event recaps, mental health tips, resource guides — practical content people share and save.', color: 'bg-indigo-600' },
              ].map(pillar => (
                <div key={pillar.num} className="bg-slate-50 rounded-xl p-5 border border-slate-200 text-center">
                  <div className={`w-10 h-10 ${pillar.color} rounded-lg flex items-center justify-center text-white font-bold text-lg mx-auto mb-3`}>{pillar.num}</div>
                  <h4 className="font-bold text-gray-900 text-sm mb-2">{pillar.title}</h4>
                  <p className="text-xs text-gray-500">{pillar.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Video & Multimedia Focus */}
          <div className="bg-gradient-to-r from-teal-700 to-cyan-700 rounded-2xl p-6 text-white mb-8" data-testid="meadows-video-strategy">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><Play size={20} /> Video & Multimedia Focus</h3>
            <p className="text-teal-100 text-sm mb-4">Produce short explainer videos (15-60 sec Reels/TikToks using CapCut), animated one-pagers, and behind-the-scenes stakeholder content.</p>
            <div className="bg-white/15 rounded-xl p-5 border border-white/20">
              <p className="text-sm text-white/90 font-medium mb-2">Example:</p>
              <p className="text-sm text-teal-100">Turn a program brief into a 30-second Reel series — a format that <strong className="text-white">increased engagement 250%+</strong> in similar campaigns I've run.</p>
            </div>
          </div>

          {/* Content Calendar */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200 mb-8" data-testid="meadows-calendar">
            <h3 className="font-bold text-gray-900 mb-4 text-lg flex items-center gap-2"><Calendar size={20} className="text-teal-600" /> Content Calendar</h3>
            <p className="text-sm text-gray-500 mb-4">Monthly planning with internal team input:</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-teal-50 rounded-xl p-4 border border-teal-200">
                <h4 className="font-bold text-teal-700 mb-2 text-sm">Weekly Output</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2"><CheckCircle size={14} className="text-teal-500 mt-0.5" /> 3-4 platform posts per week</li>
                  <li className="flex items-start gap-2"><CheckCircle size={14} className="text-teal-500 mt-0.5" /> LinkedIn deep-dive article</li>
                  <li className="flex items-start gap-2"><CheckCircle size={14} className="text-teal-500 mt-0.5" /> Instagram Reel</li>
                  <li className="flex items-start gap-2"><CheckCircle size={14} className="text-teal-500 mt-0.5" /> X/Twitter thread</li>
                </ul>
              </div>
              <div className="bg-cyan-50 rounded-xl p-4 border border-cyan-200">
                <h4 className="font-bold text-cyan-700 mb-2 text-sm">Monthly Output</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2"><CheckCircle size={14} className="text-cyan-500 mt-0.5" /> 1 long-form blog/brief repurposed across channels</li>
                  <li className="flex items-start gap-2"><CheckCircle size={14} className="text-cyan-500 mt-0.5" /> Live Q&A or event recap</li>
                  <li className="flex items-start gap-2"><CheckCircle size={14} className="text-cyan-500 mt-0.5" /> Monthly analytics report</li>
                  <li className="flex items-start gap-2"><CheckCircle size={14} className="text-cyan-500 mt-0.5" /> Content performance review & optimization</li>
                </ul>
              </div>
            </div>
          </div>

          {/* KPIs & Analytics */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200 mb-8" data-testid="meadows-kpis">
            <h3 className="font-bold text-gray-900 mb-4 text-lg flex items-center gap-2"><BarChart3 size={20} className="text-teal-600" /> KPIs & Analytics</h3>
            <p className="text-sm text-gray-500 mb-6">Using Sprout Social (or your preferred tool) + Google Analytics for monthly reports and real-time optimization.</p>
            <div className="grid md:grid-cols-4 gap-4">
              {[
                { metric: 'Engagement Rate', target: '+20%', desc: 'Likes, shares, comments relative to reach per platform' },
                { metric: 'Video Views', target: '2x', timeframe: 'first 90 days', desc: 'Watch time, view count, and subscriber conversions' },
                { metric: 'Content Reach', target: '2x', timeframe: '6 months', desc: 'Total impressions and unique reach per content pillar' },
                { metric: 'Audience Growth', target: '15% QoQ', desc: 'Follower growth across all platforms quarter over quarter' },
              ].map(kpi => (
                <div key={kpi.metric} className="bg-slate-50 rounded-xl p-4 border border-slate-200 text-center">
                  <p className="font-bold text-teal-600 text-2xl mb-1">{kpi.target}</p>
                  <p className="font-medium text-gray-800 text-sm">{kpi.metric}</p>
                  {kpi.timeframe && <p className="text-xs text-teal-600 font-medium">in {kpi.timeframe}</p>}
                  <p className="text-xs text-gray-500 mt-2">{kpi.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Collaboration */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200" data-testid="meadows-collab">
            <h3 className="font-bold text-gray-900 mb-3 text-lg flex items-center gap-2"><Users size={20} className="text-teal-600" /> Collaboration</h3>
            <p className="text-gray-600 text-sm">Weekly check-ins with the Communications team and stakeholders to align on upcoming work/events — <strong>independent execution</strong> while keeping everything on time and on brand.</p>
          </div>
        </div>
      </section>

      {/* Tools & Platforms */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <h3 className="font-bold text-gray-900 mb-6 text-center">Tools & Platforms</h3>
          <div className="flex flex-wrap justify-center gap-3">
            {['Sprout Social', 'Meta Business Suite', 'Hootsuite', 'Later', 'Canva', 'Adobe Creative Suite', 'CapCut', 'Google Analytics', 'YouTube Studio', 'WordPress', 'ChatGPT (Content Assist)', 'WTS Social Builder', 'WTS Turbo SEO'].map(tool => (
              <span key={tool} className="px-4 py-2 bg-slate-100 rounded-full text-sm text-gray-700 font-medium border border-slate-200">{tool}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Why I'm the Right Fit */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-700 to-cyan-800 text-white" data-testid="meadows-why-me">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold mb-3">Why I'm the Right Fit</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/10 rounded-xl p-5 border border-white/20">
              <h4 className="text-teal-300 text-xs font-semibold uppercase mb-2">Proven Video/Multimedia Results</h4>
              <p className="text-sm text-white/90">500K+ TikTok views, 127K video views driving 68% traffic/sales lift, Reels that sparked influencer coverage.</p>
            </div>
            <div className="bg-white/10 rounded-xl p-5 border border-white/20">
              <h4 className="text-teal-300 text-xs font-semibold uppercase mb-2">Complex-to-Simple Writing</h4>
              <p className="text-sm text-white/90">Turned technical dental services and real estate developments into persuasive, audience-friendly copy (see Fort Worth Dental case study and portfolio examples).</p>
            </div>
            <div className="bg-white/10 rounded-xl p-5 border border-white/20">
              <h4 className="text-teal-300 text-xs font-semibold uppercase mb-2">Strategy & Leadership</h4>
              <p className="text-sm text-white/90">Built content calendars, managed cross-team projects, delivered 200+ qualified leads and 75%+ CPC reductions.</p>
            </div>
            <div className="bg-white/10 rounded-xl p-5 border border-white/20">
              <h4 className="text-teal-300 text-xs font-semibold uppercase mb-2">Tools & Texas Focus</h4>
              <p className="text-sm text-white/90">Experienced with Meta Suite, Hootsuite, Later, Canva/Adobe, and analytics platforms; fully remote-capable from DFW and available for up to 10% statewide travel.</p>
            </div>
          </div>
          <div className="mt-8 bg-white/10 rounded-xl p-5 border border-white/20 text-center">
            <p className="text-sm text-teal-100"><strong className="text-white">Based in Arlington, TX</strong> — fully available for the preferred Texas location and up to 10% travel. Reliable transportation and ready to hit the ground running.</p>
          </div>
        </div>
      </section>

      {/* Next Steps / CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white text-center" data-testid="meadows-cta">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Next Steps</h2>
          <p className="text-gray-600 mb-8">Let's schedule a 30-minute call to discuss implementation and answer any questions. I'm ready to hit the ground running and help the Meadows Institute achieve its vision.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <a href="mailto:me@sierrascala.com" className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl transition-colors flex items-center gap-2" data-testid="meadows-cta-email"><Mail size={18} /> me@sierrascala.com</a>
            <a href="tel:8174700933" className="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold rounded-xl transition-colors" data-testid="meadows-cta-phone">817.470.0933</a>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="/Sierra_Scala_Resume.pdf" download="Sierra_Scala_Resume.pdf" className="px-6 py-3 bg-slate-800 hover:bg-slate-900 text-white font-semibold rounded-xl transition-colors flex items-center gap-2" data-testid="meadows-download-resume"><Download size={18} /> Download Resume</a>
            <a href="https://sierrascala.com" target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold rounded-xl transition-colors flex items-center gap-2" data-testid="meadows-view-portfolio"><Briefcase size={18} /> View Full Portfolio</a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 px-4 bg-slate-900 text-center">
        <p className="text-slate-500 text-sm">
          © {new Date().getFullYear()} Sierra Scala | <a href="/" className="text-teal-400 hover:text-teal-300">View Portfolio</a>
        </p>
      </footer>
    </div>
  );
};


// Opt Digital - Local SEO Specialist Proposal

export default MeadowsLandingPage;
